import { useEffect, useState, type ReactNode } from "react";
import { Spinner } from "../../shared/ui/index.js";
import { useToast } from "../../shared/toast.js";
import {
  identityStore,
  registerGuest,
  tokenStore,
  useGuestEvent,
  type GuestIdentity,
} from "./guest.api.js";
import { CaptureTab } from "./CaptureTab.js";
import { GalleryView } from "./GalleryView.js";
import { MyUploadsView } from "./MyUploadsView.js";
import { IdentitySheet } from "./IdentitySheet.js";
import { DetailsSheet } from "./DetailsSheet.js";
import { WallFeed } from "../wall/WallFeed.js";

export type TabId = "capture" | "wall" | "gallery" | "mine";
const isEmail = (v: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());

const WALL_TAB = {
  id: "wall" as const,
  label: "Mur",
  icon: (
    <>
      <path d="M4 5h16M4 12h16M4 19h10" strokeLinecap="round" />
      <path d="M18 17l1.5 1.5L23 15" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
};

const TABS: { id: TabId; label: string; icon: ReactNode }[] = [
  {
    id: "capture",
    label: "Caméra",
    icon: (
      <>
        <path d="M4 8a2 2 0 0 1 2-2h1.5l1-1.5h5l1 1.5H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
        <circle cx="12" cy="12.5" r="3.2" />
      </>
    ),
  },
  {
    id: "gallery",
    label: "Galerie",
    icon: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </>
    ),
  },
  {
    id: "mine",
    label: "Mes envois",
    icon: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" />
      </>
    ),
  },
];

export function GuestApp({
  slug,
  t,
  defaultTab = "capture",
}: {
  slug: string;
  t?: string;
  defaultTab?: TabId;
}) {
  const toast = useToast();
  useEffect(() => {
    if (t) tokenStore.save(slug, t);
  }, [t, slug]);
  const token = t ?? tokenStore.load(slug);

  const { data, isLoading } = useGuestEvent(slug, token);

  const [identity, setIdentity] = useState<GuestIdentity>(() => identityStore.load());
  const identityValid = identity.name.trim().length > 0 && isEmail(identity.email);
  const [idSheet, setIdSheet] = useState(false);
  const [details, setDetails] = useState(false);
  const [tab, setTab] = useState<TabId>(defaultTab);

  // 1re visite : on demande l'identité tout de suite.
  useEffect(() => {
    if (!identityValid) setIdSheet(true);
  }, [identityValid]);

  if (isLoading) return <Center theme="red-blooms"><Spinner /></Center>;
  if (!data) return <Center theme="red-blooms">Événement introuvable.</Center>;

  if (!data.tokenValid) {
    return (
      <Center theme={data.event.theme}>
        <span className="text-4xl">🔗</span>
        <p className="mt-3 text-sm" style={{ color: "var(--theme-muted)" }}>
          Ce lien est invalide ou incomplet. Scannez le QR code présent à l'événement.
        </p>
      </Center>
    );
  }

  const couple = `${data.wedding.partner1Name} & ${data.wedding.partner2Name}`;
  const initial = (identity.name.trim()[0] ?? "?").toUpperCase();

  return (
    <div
      data-theme={data.event.theme}
      className="flex h-dvh flex-col overflow-hidden"
      style={{ background: "var(--theme-bg)" }}
    >
      {/* header */}
      <header
        className="safe-top flex shrink-0 items-center justify-between gap-3 px-4 pb-2"
        style={{ borderBottom: "1px solid var(--theme-border)", background: "var(--theme-surface)" }}
      >
        <div className="min-w-0 pt-2">
          <p className="truncate font-serif text-base leading-tight" style={{ color: "var(--theme-text)" }}>
            {couple}
          </p>
          <button
            onClick={() => setDetails(true)}
            className="truncate text-xs underline-offset-2 hover:underline"
            style={{ color: "var(--theme-muted)" }}
          >
            {data.event.name} · infos
          </button>
        </div>
        <button
          onClick={() => setIdSheet(true)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
          style={{ background: "var(--theme-accent-soft)", color: "var(--theme-text)" }}
          aria-label="Mon identité"
        >
          {initial}
        </button>
      </header>

      {/* content */}
      <div className="relative min-h-0 flex-1">
        {tab === "capture" && (
          <CaptureTab
            data={data}
            token={token}
            identity={identity}
            identityValid={identityValid}
            onNeedIdentity={() => setIdSheet(true)}
          />
        )}
        {tab === "wall" && data.event.wallEnabled && (
          <WallFeed eventId={data.event.id} token={token} email={identity.email} />
        )}
        {tab === "gallery" && (
          <GalleryView
            eventId={data.event.id}
            token={token}
            identity={identity}
            identityValid={identityValid}
            onNeedIdentity={() => setIdSheet(true)}
          />
        )}
        {tab === "mine" && (
          <MyUploadsView eventId={data.event.id} identity={identity} token={token} />
        )}
      </div>

      {/* bottom nav */}
      <nav
        className="safe-bottom flex shrink-0"
        style={{ borderTop: "1px solid var(--theme-border)", background: "var(--theme-surface)" }}
      >
        {(data.event.wallEnabled
          ? [TABS[0]!, WALL_TAB, ...TABS.slice(1)]
          : TABS
        ).map((tb) => {
          const active = tab === tb.id;
          return (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className="flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px]"
              style={{ color: active ? "var(--theme-primary)" : "var(--theme-muted)" }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                {tb.icon}
              </svg>
              {tb.label}
            </button>
          );
        })}
      </nav>

      <IdentitySheet
        open={idSheet}
        initial={identity}
        dismissible={identityValid}
        onClose={() => setIdSheet(false)}
        onSave={(id) => {
          identityStore.save(id);
          setIdentity(id);
          setIdSheet(false);
          if (token) {
            registerGuest(slug, token, id)
              .then((r) => r.emailed && toast("Un email de confirmation vous a été envoyé"))
              .catch(() => undefined);
          }
        }}
      />
      <DetailsSheet open={details} onClose={() => setDetails(false)} data={data} />
    </div>
  );
}

function Center({ theme, children }: { theme: string; children: ReactNode }) {
  return (
    <div
      data-theme={theme}
      className="flex h-dvh flex-col items-center justify-center overflow-hidden px-8 text-center"
      style={{ background: "var(--theme-bg)" }}
    >
      {children}
    </div>
  );
}
